import React, { useState, useEffect } from 'react';
import { 
  Calendar, Clock, Plus, Trash2, Save, Loader2, CalendarX, RefreshCw
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { availabilityApi } from '../../lib/api';
import { toast } from 'sonner';

const DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const TIME_OPTIONS = [];
for (let h = 6; h <= 21; h++) {
  for (let m = 0; m < 60; m += 30) {
    const time = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    TIME_OPTIONS.push(time);
  }
}

const AvailabilityManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [newBlockedDate, setNewBlockedDate] = useState({ date: '', reason: '' });
  const [activeTab, setActiveTab] = useState('hours'); // hours, blocked

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [settingsRes, blockedRes] = await Promise.all([
        availabilityApi.getSettings(),
        availabilityApi.getBlockedDates()
      ]);
      setSettings(settingsRes.data);
      setBlockedDates(blockedRes.data);
    } catch (err) {
      toast.error('Failed to load availability settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await availabilityApi.updateSettings(settings);
      toast.success('Availability settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateDayHours = (dayKey, field, value) => {
    setSettings(prev => ({
      ...prev,
      weekly_hours: prev.weekly_hours.map(day => 
        day.day === dayKey ? { ...day, [field]: value } : day
      )
    }));
  };

  const handleBlockDate = async () => {
    if (!newBlockedDate.date) {
      toast.error('Please select a date');
      return;
    }
    try {
      await availabilityApi.blockDate(newBlockedDate);
      toast.success('Date blocked');
      setNewBlockedDate({ date: '', reason: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to block date');
    }
  };

  const handleUnblockDate = async (blockedId) => {
    try {
      await availabilityApi.unblockDate(blockedId);
      toast.success('Date unblocked');
      loadData();
    } catch (err) {
      toast.error('Failed to unblock date');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#1F2A44]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="availability-manager">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif text-[#222222]">Scheduling & Availability</h2>
          <p className="text-sm text-[#555555]">Manage consultation availability and blocked dates</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'hours' ? 'default' : 'outline'}
            onClick={() => setActiveTab('hours')}
            className={activeTab === 'hours' ? 'bg-[#1F2A44]' : ''}
          >
            <Clock className="w-4 h-4 mr-2" />
            Weekly Hours
          </Button>
          <Button
            variant={activeTab === 'blocked' ? 'default' : 'outline'}
            onClick={() => setActiveTab('blocked')}
            className={activeTab === 'blocked' ? 'bg-[#1F2A44]' : ''}
          >
            <CalendarX className="w-4 h-4 mr-2" />
            Blocked Dates ({blockedDates.length})
          </Button>
        </div>
      </div>

      {/* Weekly Hours Tab */}
      {activeTab === 'hours' && settings && (
        <div className="space-y-4">
          {/* General Settings */}
          <div className="bg-white border border-[#E8E4DC] rounded p-4">
            <h3 className="font-semibold text-[#222222] mb-4">General Settings</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label>Slot Duration (minutes)</Label>
                <Select 
                  value={String(settings.slot_duration)} 
                  onValueChange={(v) => setSettings(prev => ({ ...prev, slot_duration: parseInt(v) }))}
                >
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="45">45 minutes</SelectItem>
                    <SelectItem value="60">60 minutes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Buffer Between Appointments</Label>
                <Select 
                  value={String(settings.buffer_time || 0)} 
                  onValueChange={(v) => setSettings(prev => ({ ...prev, buffer_time: parseInt(v) }))}
                >
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">No buffer</SelectItem>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Advance Booking (days)</Label>
                <Input 
                  type="number" 
                  value={settings.advance_booking_days || 60} 
                  onChange={(e) => setSettings(prev => ({ ...prev, advance_booking_days: parseInt(e.target.value) }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Minimum Notice (hours)</Label>
                <Input 
                  type="number" 
                  value={settings.min_notice_hours || 24} 
                  onChange={(e) => setSettings(prev => ({ ...prev, min_notice_hours: parseInt(e.target.value) }))}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Weekly Schedule */}
          <div className="bg-white border border-[#E8E4DC] rounded p-4">
            <h3 className="font-semibold text-[#222222] mb-4">Weekly Schedule</h3>
            <div className="space-y-3">
              {DAYS.map(({ key, label }) => {
                const dayConfig = settings.weekly_hours?.find(d => d.day === key) || { 
                  day: key, enabled: false, start_time: '09:00', end_time: '17:00' 
                };
                return (
                  <div key={key} className={`p-3 rounded border ${dayConfig.enabled ? 'border-[#E8E4DC] bg-white' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-28">
                        <div className="flex items-center gap-2">
                          <Switch 
                            checked={dayConfig.enabled} 
                            onCheckedChange={(checked) => updateDayHours(key, 'enabled', checked)}
                          />
                          <span className={`font-medium ${dayConfig.enabled ? 'text-[#222222]' : 'text-gray-400'}`}>{label}</span>
                        </div>
                      </div>
                      
                      {dayConfig.enabled && (
                        <>
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-[#555555]">Start:</Label>
                            <Select value={dayConfig.start_time} onValueChange={(v) => updateDayHours(key, 'start_time', v)}>
                              <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {TIME_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-[#555555]">End:</Label>
                            <Select value={dayConfig.end_time} onValueChange={(v) => updateDayHours(key, 'end_time', v)}>
                              <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {TIME_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-[#555555]">Break:</Label>
                            <Select value={dayConfig.break_start || 'none'} onValueChange={(v) => updateDayHours(key, 'break_start', v === 'none' ? null : v)}>
                              <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No break</SelectItem>
                                {TIME_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            {dayConfig.break_start && (
                              <>
                                <span className="text-xs">to</span>
                                <Select value={dayConfig.break_end || '13:00'} onValueChange={(v) => updateDayHours(key, 'break_end', v)}>
                                  <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {TIME_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSaveSettings} disabled={saving} className="bg-[#1F2A44]">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Settings
            </Button>
          </div>
        </div>
      )}

      {/* Blocked Dates Tab */}
      {activeTab === 'blocked' && (
        <div className="space-y-4">
          {/* Add Blocked Date */}
          <div className="bg-white border border-[#E8E4DC] rounded p-4">
            <h3 className="font-semibold text-[#222222] mb-4">Block a Date</h3>
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <Label>Date</Label>
                <Input 
                  type="date" 
                  value={newBlockedDate.date} 
                  onChange={(e) => setNewBlockedDate(prev => ({ ...prev, date: e.target.value }))}
                  className="mt-1"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="flex-1">
                <Label>Reason (Optional)</Label>
                <Input 
                  value={newBlockedDate.reason} 
                  onChange={(e) => setNewBlockedDate(prev => ({ ...prev, reason: e.target.value }))}
                  className="mt-1"
                  placeholder="e.g., Holiday, Vacation, Conference"
                />
              </div>
              <Button onClick={handleBlockDate} className="bg-[#1F2A44]">
                <Plus className="w-4 h-4 mr-2" />
                Block Date
              </Button>
            </div>
          </div>

          {/* Blocked Dates List */}
          <div className="bg-white border border-[#E8E4DC] rounded p-4">
            <h3 className="font-semibold text-[#222222] mb-4">Blocked Dates</h3>
            {blockedDates.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-[#C2A46D] mx-auto mb-4" />
                <p className="text-[#555555]">No blocked dates. Your calendar is fully available based on weekly hours.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {blockedDates.map((blocked) => (
                  <div key={blocked.id} className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded">
                    <div className="flex items-center gap-4">
                      <CalendarX className="w-5 h-5 text-red-500" />
                      <div>
                        <p className="font-medium text-[#222222]">{formatDate(blocked.date)}</p>
                        {blocked.reason && <p className="text-sm text-[#555555]">{blocked.reason}</p>}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => handleUnblockDate(blocked.id)} className="text-red-600 hover:bg-red-100">
                      <Trash2 className="w-4 h-4 mr-1" />
                      Unblock
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AvailabilityManager;
